import { useRef, useCallback, useMemo, type CSSProperties } from 'react';
import { useAppSelector } from '../../store';
import { testIds } from '../../lib/testIds';
import {
  computeLineDiff,
  computeWordDiff,
  type TimelineStep,
  type DiffHunk,
  type WordDiffSegment,
} from '@repancy/core';

interface SideBySideDiffProps {
  originalSessionId: string;
  forkedSessionId: string;
}

const sunkenBorder: CSSProperties = {
  border: '2px solid',
  borderColor: '#808080 #dfdfdf #dfdfdf #808080',
  backgroundColor: '#ffffff',
};

const headerStyle: CSSProperties = {
  padding: '4px 8px',
  fontFamily: "var(--xp-font-body)",
  fontSize: 12,
  fontWeight: 'bold',
  backgroundColor: '#ece9d8',
  borderBottom: '1px solid #808080',
};

const columnStyle: CSSProperties = {
  flex: 1,
  overflow: 'auto',
  minWidth: 0,
};

const lineStyle = (type: DiffHunk['type']): CSSProperties => ({
  padding: '1px 8px',
  fontFamily: "'Courier New', Courier, monospace",
  fontSize: 12,
  lineHeight: '18px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  backgroundColor:
    type === 'added' ? '#e6ffe6' :
    type === 'removed' ? '#ffe6e6' :
    'transparent',
  borderLeft:
    type === 'added' ? '3px solid #008000' :
    type === 'removed' ? '3px solid #cc0000' :
    '3px solid transparent',
});

function renderWordSegments(segments: WordDiffSegment[]) {
  return segments.map((seg, i) => {
    const style: CSSProperties =
      seg.type === 'added'
        ? { fontWeight: 'bold', color: '#008000', backgroundColor: '#ccffcc' }
        : seg.type === 'removed'
        ? { fontWeight: 'bold', color: '#cc0000', backgroundColor: '#ffcccc' }
        : {};
    return (
      <span key={i} style={style}>
        {seg.text}
      </span>
    );
  });
}

function getStepsText(
  stepIds: string[],
  entities: Record<string, TimelineStep>,
  sessionId: string,
): string {
  return stepIds
    .map((id) => entities[id])
    .filter((s): s is TimelineStep => s !== undefined && s.sessionId === sessionId)
    .map((s) => s.content)
    .join('\n');
}

export function SideBySideDiff({ originalSessionId, forkedSessionId }: SideBySideDiffProps) {
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);

  const stepIds = useAppSelector((s) => s.timelines.steps.ids);
  const stepEntities = useAppSelector((s) => s.timelines.steps.entities);

  const originalText = useMemo(
    () => getStepsText(stepIds, stepEntities, originalSessionId),
    [stepIds, stepEntities, originalSessionId],
  );
  const forkedText = useMemo(
    () => getStepsText(stepIds, stepEntities, forkedSessionId),
    [stepIds, stepEntities, forkedSessionId],
  );

  const hunks = useMemo(() => computeLineDiff(originalText, forkedText), [originalText, forkedText]);

  // Split hunks into left (original) and right (forked) columns
  const { leftLines, rightLines } = useMemo(() => {
    const left: Array<{ hunk: DiffHunk | null; wordSegments?: WordDiffSegment[] }> = [];
    const right: Array<{ hunk: DiffHunk | null; wordSegments?: WordDiffSegment[] }> = [];

    let i = 0;
    while (i < hunks.length) {
      const hunk = hunks[i]!;
      if (hunk.type === 'unchanged') {
        left.push({ hunk });
        right.push({ hunk });
        i++;
      } else if (hunk.type === 'removed') {
        // Pair removed with following added for word diff
        const nextHunk = hunks[i + 1];
        if (nextHunk && nextHunk.type === 'added') {
          const wordDiffLeft = computeWordDiff(hunk.content, nextHunk.content);
          const wordDiffRight = computeWordDiff(hunk.content, nextHunk.content);
          left.push({ hunk, wordSegments: wordDiffLeft.filter((s) => s.type !== 'added') });
          right.push({ hunk: nextHunk, wordSegments: wordDiffRight.filter((s) => s.type !== 'removed') });
          i += 2;
        } else {
          left.push({ hunk });
          right.push({ hunk: null });
          i++;
        }
      } else {
        // added without preceding removed
        left.push({ hunk: null });
        right.push({ hunk });
        i++;
      }
    }
    return { leftLines: left, rightLines: right };
  }, [hunks]);

  const syncScroll = useCallback((source: 'left' | 'right') => {
    if (syncing.current) return;
    syncing.current = true;
    const from = source === 'left' ? leftRef.current : rightRef.current;
    const to = source === 'left' ? rightRef.current : leftRef.current;
    if (from && to) {
      to.scrollTop = from.scrollTop;
    }
    syncing.current = false;
  }, []);

  const forkedSession = useAppSelector((s) => s.promptSessions.sessions.entities[forkedSessionId]);
  const forkedLabel = forkedSession ? 'Fork' : 'Fork';

  const containerStyle: CSSProperties = {
    display: 'flex',
    gap: 4,
    height: '100%',
    minHeight: 200,
  };

  const emptyLineStyle: CSSProperties = {
    padding: '1px 8px',
    fontFamily: "'Courier New', Courier, monospace",
    fontSize: 12,
    lineHeight: '18px',
    backgroundColor: '#f0f0f0',
    borderLeft: '3px solid transparent',
  };

  return (
    <div style={containerStyle} data-testid={testIds.timeline.sideBySide}>
      {/* Left: Original */}
      <div style={{ ...columnStyle, ...sunkenBorder }} data-testid={testIds.timeline.diffOriginal}>
        <div style={headerStyle}>Original</div>
        <div
          ref={leftRef}
          style={{ overflow: 'auto', maxHeight: 400 }}
          onScroll={() => syncScroll('left')}
        >
          {leftLines.map((entry, i) =>
            entry.hunk ? (
              <div key={i} style={lineStyle(entry.hunk.type)}>
                {entry.wordSegments
                  ? renderWordSegments(entry.wordSegments)
                  : entry.hunk.content || ' '}
              </div>
            ) : (
              <div key={i} style={emptyLineStyle}>
                {' '}
              </div>
            ),
          )}
        </div>
      </div>

      {/* Right: Fork */}
      <div style={{ ...columnStyle, ...sunkenBorder }} data-testid={testIds.timeline.diffForked}>
        <div style={headerStyle}>{forkedLabel}</div>
        <div
          ref={rightRef}
          style={{ overflow: 'auto', maxHeight: 400 }}
          onScroll={() => syncScroll('right')}
        >
          {rightLines.map((entry, i) =>
            entry.hunk ? (
              <div key={i} style={lineStyle(entry.hunk.type)}>
                {entry.wordSegments
                  ? renderWordSegments(entry.wordSegments)
                  : entry.hunk.content || ' '}
              </div>
            ) : (
              <div key={i} style={emptyLineStyle}>
                {' '}
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
}
